import { useEffect, useState } from 'react';

const FONT_PROBES = ["690 100px 'Sora Variable'", "700 30px 'Manrope Variable'"];
const SCRIPT_PROBES = {
  hi: ["600 30px 'Noto Sans Devanagari Variable'", 'मेटाडेटा कीवर्ड'],
  bn: ["600 30px 'Noto Sans Bengali Variable'", 'মেটাডেটা কীওয়ার্ড']
};

// The compositions fit text with canvas metrics, so the faces they draw with
// (including Latin Extended for Turkish and the page's Indic face) are
// requested before the first frame. Never waits longer than 2.5 s.
const ensureShowcaseFonts = (language) => {
  if (typeof document === 'undefined' || typeof document.fonts?.load !== 'function') return Promise.resolve();
  const loads = FONT_PROBES.map((font) => document.fonts.load(font, 'Stocktag AÇĞİÖŞÜ ağıöşüç ÀÉÑß'));
  const script = SCRIPT_PROBES[language];
  if (script) loads.push(document.fonts.load(script[0], script[1]));
  return Promise.race([
    Promise.allSettled(loads),
    new Promise((resolve) => { window.setTimeout(resolve, 2500); })
  ]);
};

// One shared dynamic import: the Remotion runtime (~85 kB gzip) only loads
// when a showcase section approaches the viewport, never on the critical path.
// It is optional, so the stale-asset recovery in index.html and main.jsx must
// not reload the page when it fails. Safari's import error carries no URL to
// recognise it by: while this import is in flight (`pending`) a URL-less error
// is held back, and only the exact error object it rejects with (`claimed`) is
// left alone; any other is still recovered from.
const optionalChunks = () => window.__stocktagOptionalChunks
  || (window.__stocktagOptionalChunks = { pending: 0, claimed: new WeakSet() });

let runtimePromise = null;
export const loadShowcaseRuntime = (language = 'en') => {
  if (!runtimePromise) {
    const chunks = optionalChunks();
    chunks.pending += 1;
    runtimePromise = Promise.all([import('./remotion/index.js'), ensureShowcaseFonts(language)])
      .then(([module]) => (module?.ShowcasePlayer ? module : null))
      .catch((error) => {
        if (error && typeof error === 'object') chunks.claimed.add(error);
        runtimePromise = null;
        return null;
      })
      .finally(() => {
        chunks.pending = Math.max(0, chunks.pending - 1);
      });
  }
  return runtimePromise;
};

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

const prefersLightweightMedia = () => {
  const connection = typeof navigator !== 'undefined' ? navigator.connection : null;
  if (!connection) return false;
  return Boolean(connection.saveData) || /(^|-)2g$/.test(String(connection.effectiveType || ''));
};

const scheduleIdle = (callback) => {
  if (typeof window.requestIdleCallback === 'function') {
    const id = window.requestIdleCallback(callback, { timeout: 1500 });
    return () => window.cancelIdleCallback(id);
  }
  const id = window.setTimeout(callback, 350);
  return () => window.clearTimeout(id);
};

export const useReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia(REDUCED_MOTION_QUERY);
    const update = () => setReduced(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);
  return reduced;
};

export const useMediaQuery = (queryText) => {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia(queryText);
    const update = () => setMatches(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, [queryText]);
  return matches;
};

const INTENT_EVENTS = ['scroll', 'pointerdown', 'keydown', 'touchstart'];

// Returns the loaded Remotion runtime once `ref` is within `rootMargin` of the
// viewport. Nothing is fetched before the visitor first scrolls, taps or
// presses a key (the reel sits just below the hero, inside the margin on first
// paint), so a bounce never pays for it. Stays null on the server, on
// Save-Data / 2G connections and when the chunk fails to load, so the
// server-rendered poster remains in place.
export const useShowcaseRuntime = (ref, { rootMargin = '700px 0px', language = 'en' } = {}) => {
  const [runtime, setRuntime] = useState(null);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof window === 'undefined' || prefersLightweightMedia()) return undefined;
    let cancelled = false;
    let cancelIdle = null;
    let observer = null;

    const start = () => {
      observer?.disconnect();
      observer = null;
      cancelIdle = scheduleIdle(() => {
        loadShowcaseRuntime(language).then((module) => {
          if (!cancelled && module) setRuntime(module);
        });
      });
    };

    const disarm = () => INTENT_EVENTS.forEach((type) => window.removeEventListener(type, arm));
    function arm() {
      disarm();
      if (cancelled) return;
      if (!('IntersectionObserver' in window)) {
        start();
        return;
      }
      observer = new window.IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) start();
      }, { rootMargin });
      observer.observe(node);
    }

    if (window.scrollY > 0) {
      arm();
    } else {
      INTENT_EVENTS.forEach((type) => window.addEventListener(type, arm, { passive: true }));
    }

    return () => {
      cancelled = true;
      disarm();
      observer?.disconnect();
      cancelIdle?.();
    };
  }, [language, ref, rootMargin]);

  return runtime;
};

// Tracks whether `ref` is at least `threshold` visible.
export const useInView = (ref, { threshold = 0.5, root = null, rootMargin = '0px' } = {}) => {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node || typeof window === 'undefined' || !('IntersectionObserver' in window)) return undefined;
    const observer = new window.IntersectionObserver(([entry]) => {
      setInView(Boolean(entry?.isIntersecting && entry.intersectionRatio >= threshold));
    }, { threshold: [0, threshold, 1], root: root?.current || null, rootMargin });
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref, root, rootMargin, threshold]);
  return inView;
};
